// src/reducers/habilitacionLocal_slice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiService from "../services/apiService";
// Si quieres setear el afiliado actual del gestor en su slice:
// import { afiliadoActions } from "./afiliado_slice";

// Servicios base (mismo patrón que lugar_slice)
const habService = apiService("/habilitaciones-local"); // /api/habilitaciones-local
const afiliadoService = apiService("/afiliados");       // /api/afiliados

// Base URL para fetch (PATCH)
const API_BASE = process.env.REACT_APP_API_URL || "/api";

/* ===================== THUNKS ===================== */

// PATCH /api/habilitaciones-local/afiliados/:id/estado  (ACTIVO | INACTIVO)
export const updateAfiliadoEstado = createAsyncThunk(
  "habilitacionLocal/updateAfiliadoEstado",
  async ({ idAfiliado, estado, motivo }, { rejectWithValue }) => {
    try {
      const url = `${API_BASE}/habilitaciones-local/afiliados/${idAfiliado}/estado`;
      const token = localStorage.getItem("token");
      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          estado: String(estado || "").toUpperCase(),
          ...(motivo ? { motivo } : {}),
        }),
      });

      if (!res.ok) {
        const data = await safeJson(res);
        throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
      }
      const data = await safeJson(res);
      // backend responde { message, afiliado } o el objeto afiliado directamente
      return data?.afiliado || data || null;
    } catch (error) {
      return rejectWithValue(error?.message || "Error al actualizar estado");
    }
  }
);

// GET /api/habilitaciones-local/logs?id_afiliado=:id
export const fetchLogsByAfiliado = createAsyncThunk(
  "habilitacionLocal/fetchLogsByAfiliado",
  async (idAfiliado, { rejectWithValue }) => {
    try {
      // apiService en tu estilo soporta getAllSome('logs?...')
      const resp = await habService.getAllSome(`logs?id_afiliado=${encodeURIComponent(idAfiliado)}`);
      const data = Array.isArray(resp) ? { rows: resp, count: resp.length } : resp;
      return { idAfiliado, data };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message || "Error al obtener logs");
    }
  }
);

// (Opcional) POST /api/habilitaciones-local/logs  si decides crear logs desde el front
export const createHabilitacionLocalLog = createAsyncThunk(
  "habilitacionLocal/createHabilitacionLocalLog",
  async (payload, { rejectWithValue }) => {
    try {
      const resp = await habService.createSome("logs", payload);
      return resp;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message || "Error al registrar log");
    }
  }
);

// Asegura el afiliado del gestor (GET /api/afiliados/persona/:id_persona)
export const ensureGestorAfiliado = createAsyncThunk(
  "habilitacionLocal/ensureGestorAfiliado",
  async (id_persona, { rejectWithValue /*, dispatch*/ }) => {
    try {
      const resp = await afiliadoService.getAllBySomeId("persona", id_persona);
      // Si quieres guardar en el slice de afiliado:
      // dispatch(afiliadoActions.setCurrentAfiliado(resp));
      return resp;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message || "Error al obtener afiliado del gestor");
    }
  }
);

/* ===================== SLICE ===================== */

const initialState = {
  statusGet: "idle",
  statusSend: "idle",
  error: null,

  lastUpdatedAfiliado: null,
  logsByAfiliado: {}, // { [idAfiliado]: { rows, count } }
};

const habilitacionLocalSlice = createSlice({
  name: "habilitacionLocal",
  initialState,
  reducers: {
    resetStatusSend(state) {
      state.statusSend = "idle";
    },
    resetStatusGet(state) {
      state.statusGet = "idle";
    },
    clearLastUpdated(state) {
      state.lastUpdatedAfiliado = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // updateAfiliadoEstado
      .addCase(updateAfiliadoEstado.pending, (state) => {
        state.statusSend = "sending";
        state.error = null;
      })
      .addCase(updateAfiliadoEstado.fulfilled, (state, action) => {
        state.statusSend = "idle";
        state.lastUpdatedAfiliado = action.payload || null;
      })
      .addCase(updateAfiliadoEstado.rejected, (state, action) => {
        state.statusSend = "failed";
        state.error = action.payload || action.error?.message || "Error al actualizar estado";
      })

      // fetchLogsByAfiliado
      .addCase(fetchLogsByAfiliado.pending, (state) => {
        state.statusGet = "loading";
        state.error = null;
      })
      .addCase(fetchLogsByAfiliado.fulfilled, (state, action) => {
        state.statusGet = "idle";
        const { idAfiliado, data } = action.payload || {};
        if (idAfiliado != null) {
          state.logsByAfiliado[idAfiliado] = data || { rows: [], count: 0 };
        }
      })
      .addCase(fetchLogsByAfiliado.rejected, (state, action) => {
        state.statusGet = "failed";
        state.error = action.payload || action.error?.message || "Error al obtener logs";
      })

      // createHabilitacionLocalLog
      .addCase(createHabilitacionLocalLog.pending, (state) => {
        state.statusSend = "sending";
        state.error = null;
      })
      .addCase(createHabilitacionLocalLog.fulfilled, (state) => {
        state.statusSend = "idle";
      })
      .addCase(createHabilitacionLocalLog.rejected, (state, action) => {
        state.statusSend = "failed";
        state.error = action.payload || action.error?.message || "Error al registrar log";
      })

      // ensureGestorAfiliado
      .addCase(ensureGestorAfiliado.pending, (state) => {
        state.statusGet = "loading";
      })
      .addCase(ensureGestorAfiliado.fulfilled, (state) => {
        state.statusGet = "idle";
      })
      .addCase(ensureGestorAfiliado.rejected, (state, action) => {
        state.statusGet = "failed";
        state.error = action.payload || action.error?.message || "Error al obtener afiliado del gestor";
      });
  },
});

export const { resetStatusSend, resetStatusGet, clearLastUpdated } =
  habilitacionLocalSlice.actions;

export default habilitacionLocalSlice.reducer;

/* ===================== HELPERS ===================== */
async function safeJson(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}
